import { Link } from "react-router-dom";
import LegalPage, { H2, P, UL } from "@/components/LegalPage";
import { BUSINESS_ADDRESS, PHONE_DISPLAY } from "@/lib/whatsapp";
import { SITE_URL, SITE_HOST } from "@/lib/site";

export default function Terms() {
  return (
    <LegalPage
      title="Terms & Conditions"
      effectiveDate="08 May 2026"
      path="/terms"
      description={`The terms that govern your use of ${SITE_HOST} and our online yoga programs — including health disclaimers, fees, cancellations and governing law.`}
    >
      <P>
        These Terms & Conditions ("Terms") govern your use of the website{" "}
        <a className="text-forest underline" href={SITE_URL}>
          {SITE_HOST}
        </a>{" "}
        and any online yoga consultations, classes or transformation programs
        ("Services") delivered by <strong>Weight Loss Gurukul</strong>{" "}
        ("we", "us", "our"). By using our Services, you agree to these Terms
        and to our{" "}
        <Link to="/privacy" className="text-forest underline">
          Privacy Policy
        </Link>
        .
      </P>

      <H2>1. Eligibility</H2>
      <UL>
        <li>You must be 18 years or older to enrol.</li>
        <li>
          You confirm you are physically and mentally able to take part in
          yoga, breathwork and lifestyle coaching, and that you have consulted
          a qualified medical professional if you have any pre-existing
          condition (including but not limited to cardiac issues, hypertension,
          pregnancy, diabetes, recent surgery, slipped disc, glaucoma).
        </li>
      </UL>

      <H2>2. Health & safety disclaimer</H2>
      <P>
        Our Services are educational and wellness-focused. They are{" "}
        <strong>not a substitute for medical advice, diagnosis or treatment</strong>.
        You participate at your own risk and must immediately stop any
        practice that causes pain, dizziness or discomfort, and seek medical
        help. We are not responsible for injuries, conditions or outcomes
        arising from practice without due care or against medical advice.
      </P>

      <H2>3. Booking, scheduling & attendance</H2>
      <UL>
        <li>
          Free consultations are scheduled by mutual agreement on WhatsApp or
          email and may be rescheduled with reasonable notice.
        </li>
        <li>
          Live sessions are held on a video platform we will share. You are
          responsible for a stable internet connection and a safe, clutter-free
          practice space.
        </li>
        <li>
          Missed live sessions can usually be made up via recordings or a
          rescheduled slot, subject to teacher availability.
        </li>
      </UL>

      <H2>4. Fees, taxes & payment</H2>
      <P>
        Program fees are quoted during your consultation and confirmed in
        writing before you enrol. Prices may be subject to applicable taxes
        (such as GST in India) and are payable in full unless an instalment
        plan is agreed. Currencies and amounts are confirmed at the time of
        purchase.
      </P>

      <H2>5. 7-day money-back guarantee & refunds</H2>
      <P>
        Paid programs are covered by a <strong>7-day money-back guarantee</strong>{" "}
        from the date your program officially begins, subject to the conditions
        in our{" "}
        <Link to="/refund" className="text-forest underline">
          Refund Policy
        </Link>
        . Free consultations are not refundable as they are complimentary.
      </P>

      <H2>6. Your responsibilities</H2>
      <UL>
        <li>Provide accurate information and update us if anything changes (especially health-relevant information).</li>
        <li>Show up to scheduled sessions on time and follow your teacher's instructions.</li>
        <li>Respect other clients and our team — abusive, discriminatory or unlawful conduct may end your enrolment without refund.</li>
        <li>Do not record, share, resell or distribute our live sessions, recordings or materials in any form.</li>
      </UL>

      <H2>7. Intellectual property</H2>
      <P>
        All website content, brand assets, sequences, manuals, recordings,
        music, images and program materials are owned by Weight Loss Gurukul
        or licensed to us. You receive a personal, non-transferable, revocable
        right to use them for your individual practice only. Any other use
        requires our prior written permission.
      </P>

      <H2>8. Third-party services</H2>
      <P>
        Our website links to third-party services (e.g. WhatsApp, Google
        Analytics, payment processors, Resend email). We are not responsible
        for the content or practices of those services. Their terms and
        privacy policies apply to your use of them.
      </P>

      <H2>9. Testimonials & results</H2>
      <P>
        Testimonials, before/after stories and case studies on our website
        reflect individual experiences. Results vary depending on your
        starting point, consistency, lifestyle and adherence to the program.
        We do not guarantee any specific weight loss, body composition,
        fitness or health outcome.
      </P>

      <H2>10. Limitation of liability</H2>
      <P>
        To the maximum extent permitted by law, Weight Loss Gurukul, its
        founders, teachers, employees and affiliates will not be liable for
        any indirect, incidental, special, consequential or punitive damages,
        or any loss of profits or revenues, arising out of your use of the
        Services. Our total aggregate liability for any claim is capped at the
        fees you paid for the program in question in the 12 months preceding
        the claim.
      </P>

      <H2>11. Termination</H2>
      <P>
        We may suspend or terminate your access if you breach these Terms,
        engage in unsafe or abusive behaviour, or fail to pay applicable fees.
        Termination does not affect rights and obligations that, by their
        nature, should survive — including IP, liability and dispute
        resolution.
      </P>

      <H2>12. Changes to these Terms</H2>
      <P>
        We may update these Terms from time to time. The "Effective date"
        at the top tells you when. Continued use of the Services after the
        change constitutes acceptance of the updated Terms.
      </P>

      <H2>13. Governing law & jurisdiction</H2>
      <P>
        These Terms are governed by the laws of India. Any dispute arising
        out of or in connection with these Terms or your use of the Services
        will be subject to the exclusive jurisdiction of the courts of
        Bangalore, Karnataka, India.
      </P>

      <H2>14. Contact</H2>
      <P>
        Weight Loss Gurukul<br />
        {BUSINESS_ADDRESS.line1}, {BUSINESS_ADDRESS.line2},{" "}
        {BUSINESS_ADDRESS.line3}, {BUSINESS_ADDRESS.line4}<br />
        Email:{" "}
        <a className="text-forest underline" href="mailto:hello@weightlossgurukul.com">
          hello@weightlossgurukul.com
        </a>{" "}
        · Phone: {PHONE_DISPLAY}
      </P>
    </LegalPage>
  );
}
